import * as moment from 'moment';
import { environment } from '../../../../environments/environment';
import { Option } from '../../../../interfaces/dropdown-option.interface';
import { CalculateOfferPayload } from '../../shared/interfaces/calculate-offer-payload.interface';
import { FirstStepPayload } from '../../shared/interfaces/first-step-payload.interface';
import { PolicyCreatorStateModel } from '../store/policy-creator.model';
import { checkForInstalments } from './check-for-instalments';
import { newIdefendConditionsMapper } from './idefend-mappers';
import * as Sentry from '@sentry/angular';

export function offerPayloadMapper(
  state: PolicyCreatorStateModel,
  partialPayload = false,
): CalculateOfferPayload {
  try {
    if (!state.portfolioSelected) {
      return null;
    }

    if (!state.firstStepPayload?.selectedVehicleModel) {
      return null;
    }

    /* usageTypeCode */
    let usageTypeCode: string;
    const hasOptionSelected = state.firstStepPayload?.vehicleType.options.find(
      (option) => option.isSelected,
    );

    if (hasOptionSelected) {
      usageTypeCode = 'BUSINESS';
    } else {
      usageTypeCode = 'INDIVIDUAL';
    }

    if (
      state.firstStepPayload?.vehiclePolicyPriceType !== 'Brutto' ||
      state.firstStepPayload?.vehiclePriceType !== 'Brutto'
    ) {
      usageTypeCode = 'BUSINESS';
    }

    const vatReclaimable = (firstStepPayload: FirstStepPayload): string => {
      if (
        (state.firstStepPayload?.vehiclePolicyPriceType === 'Netto' &&
          state.firstStepPayload?.vehiclePriceType === 'Netto') ||
        (state.firstStepPayload?.vehiclePolicyPriceType === 'Netto' &&
          state.firstStepPayload?.vehiclePriceType === 'Brutto') ||
        (state.firstStepPayload?.vehiclePolicyPriceType === 'Netto' &&
          state.firstStepPayload?.vehiclePriceType === '50%')
      ) {
        return 'YES';
      }

      if (
        (state.firstStepPayload?.vehiclePolicyPriceType === 'Brutto' &&
          state.firstStepPayload?.vehiclePriceType === 'Brutto') ||
        (state.firstStepPayload?.vehiclePolicyPriceType === 'Brutto' &&
          state.firstStepPayload?.vehiclePriceType === 'Netto') ||
        (state.firstStepPayload?.vehiclePolicyPriceType === 'Brutto' &&
          state.firstStepPayload?.vehiclePriceType === '50%')
      ) {
        return 'NO';
      }

      if (
        state.firstStepPayload.vehiclePolicyPriceType === '50%' ||
        state.firstStepPayload?.vehiclePriceType === '50%'
      ) {
        return 'HALF';
      }
    };

    const usageCode = (options: Option[]): string => {
      const selectedOptionsFound = options
        .filter((option) => option.isSelected)
        .map((option) => option.code);
      if (selectedOptionsFound.length) {
        return selectedOptionsFound[0];
      } else {
        return 'STANDARD';
      }
    };

    const vehiclePrice = (firstStepPayload: FirstStepPayload): number => {
      if (
        firstStepPayload?.selectedInsuranceVariant === 'FLEX' ||
        firstStepPayload?.selectedInsuranceVariant === 'FLEX GO'
      ) {
        return Number(firstStepPayload.vehiclePrice) * 100;
      }

      const { vehiclePolicyPrice, vehiclePrice, vehiclePriceAc } =
        firstStepPayload;
      const vehiclePolicyPriceNumber = Number(vehiclePolicyPrice) * 100;
      const vehiclePriceNumber = Number(vehiclePrice) * 100;
      const vehiclePriceAcNumber = Number(vehiclePriceAc) * 100;

      if (vehiclePolicyPriceNumber && !!vehiclePriceNumber) {
        return vehiclePolicyPriceNumber <= vehiclePriceNumber
          ? vehiclePolicyPriceNumber
          : vehiclePriceNumber;
      } else {
        return vehiclePolicyPriceNumber <= vehiclePriceAcNumber
          ? vehiclePolicyPriceNumber
          : vehiclePriceAcNumber;
      }
    };

    const purchasePrice = (vehiclePrice) => {
      if (state.firstStepPayload?.selectedInsuranceVariant === 'MAX AC') {
        if (state.firstStepPayload?.vehiclePolicyPriceType === 'Netto') {
          return Math.round((vehiclePrice * 1.23) / 100) * 100;
        }

        if (state.firstStepPayload?.vehiclePolicyPriceType === 'Brutto') {
          return vehiclePrice;
        }

        if (state.firstStepPayload?.vehiclePolicyPriceType === '50%') {
          return Math.round((vehiclePrice * 1.103137143) / 100) * 100;
        }
      }

      if (state.firstStepPayload?.vehiclePriceType === 'Netto') {
        return Math.round((vehiclePrice * 1.23) / 100) * 100;
      }

      if (state.firstStepPayload?.vehiclePriceType === 'Brutto') {
        return vehiclePrice;
      }

      if (state.firstStepPayload?.vehiclePriceType === '50%') {
        return Math.round((vehiclePrice * 1.103137143) / 100) * 100;
      }
    };

    const purchasePriceNet = (vehiclePrice) => {
      if (state.firstStepPayload?.selectedInsuranceVariant === 'MAX AC') {
        if (state.firstStepPayload?.vehiclePolicyPriceType === 'Netto') {
          return vehiclePrice;
        }

        if (state.firstStepPayload?.vehiclePolicyPriceType === 'Brutto') {
          return Math.round(vehiclePrice / 1.23 / 100) * 100;
        }

        if (state.firstStepPayload?.vehiclePolicyPriceType === '50%') {
          return Math.round(vehiclePrice / 1.115 / 100) * 100;
        }
      }

      if (state.firstStepPayload?.vehiclePriceType === 'Netto') {
        return vehiclePrice;
      }

      if (state.firstStepPayload?.vehiclePriceType === 'Brutto') {
        return Math.round(vehiclePrice / 1.23 / 100) * 100;
      }

      if (state.firstStepPayload?.vehiclePriceType === '50%') {
        return Math.round(vehiclePrice / 1.115 / 100) * 100;
      }
    };

    const purchasePriceInputType = (firstStepPayload: FirstStepPayload) => {
      if (
        (firstStepPayload?.vehiclePolicyPriceType === 'Brutto' &&
          firstStepPayload?.vehiclePriceType === 'Netto') ||
        (firstStepPayload?.vehiclePolicyPriceType === '50%' &&
          firstStepPayload?.vehiclePriceType === 'Netto')
      ) {
        return 'WITHOUT_VAT';
      }

      if (
        firstStepPayload?.vehiclePolicyPriceType === 'Brutto' &&
        firstStepPayload?.vehiclePriceType === '50%'
      ) {
        return 'WITH_VAT';
      }

      if (
        firstStepPayload?.vehiclePriceType === 'Netto' &&
        firstStepPayload?.vehiclePolicyPriceType === 'Netto'
      ) {
        return 'WITHOUT_VAT';
      } else {
        return 'WITH_VAT';
      }
    };

    const categoryCode = state.vehicleCategories.find(
      (category) =>
        category.id === state.firstStepPayload?.selectedVehicleModel.categoryId,
    ).code;

    if (!state.secondStepPayload) {
      if (!partialPayload) {
        return null;
      }
    }

    const responseDto: CalculateOfferPayload = {
      productCode: state.portfolioSelected.productCode,
      saleInitiatedOn: moment().format(),
      sellerNodeCode: environment.SELLER_NODE_CODE,
      instalmentSplittingSchemeCode: checkForInstalments(state),
      vehicleSnapshot: {
        firstRegisteredOn: moment(
          state.firstStepPayload?.vehicleRegDate,
        ).format('YYYY-MM-DD'),
        categoryCode: categoryCode,
        purchasePrice: purchasePrice(vehiclePrice(state.firstStepPayload)),
        purchasePriceNet: purchasePriceNet(
          vehiclePrice(state.firstStepPayload),
        ),
        purchasePriceInputType: purchasePriceInputType(state.firstStepPayload),
        usageTypeCode: usageTypeCode,
        purchasePriceVatReclaimableCode: vatReclaimable(state.firstStepPayload),
        usageCode: usageCode(state.firstStepPayload?.vehicleType.options),
        modelCode: state.firstStepPayload?.selectedVehicleModel.code,
      },
      options: partialPayload
        ? null
        : {
            CLAIM_LIMIT: `${state.secondStepPayload.insuranceLimit}`,
            TERM: `${state.secondStepPayload.policyPeriod}`,
            PAYMENT_TERM:
              state.secondStepPayload?.paymentType === 'PT_LS_AIS'
                ? 'PT_LS'
                : state.secondStepPayload?.paymentType,
            PAYMENT_METHOD: state.paymentMethod ?? 'PM_BT',
          },
    };

    newIdefendConditionsMapper(
      state.firstStepPayload,
      state.secondStepPayload,
      responseDto,
    );

    return responseDto;
  } catch (e) {
    console.error('offer-payload-mapper error:', e);
    Sentry.captureException(e, {
      tags: {
        location: 'offerPayloadMapper',
        portfolioCode: state.portfolioSelected?.productCode,
      },
      extra: {
        state: state,
        partialPayload: partialPayload,
      },
    });
    return null;
  }
}
